import {
  AvatarProps,
  AvatarGroup,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  FAIconName,
  FireflyButton,
  Icon,
  Tag,
  TagColor,
  TooltipAvatar,
} from '@equip.health/ui';

import { useHistory } from 'react-router-dom';

import {
  APPOINTMENT_STATUS,
  AppointmentParticipant,
  AppointmentStatus,
  AppointmentViewDetail,
  PARTICIPANT_TYPE,
  PatientProviderDashboard,
} from '~/store/services/providerDashboard';
import { Path } from '~/routes/constants';
import { formatAppointmentTimeRange } from '../provider-dashboard.util';
import PatientIdentity from './PatientIdentity';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface AppointmentCardProps {
  patient: PatientProviderDashboard;
  appointment: AppointmentViewDetail;
  isPast?: boolean;
  showJoin?: boolean;
  onJoin?: (appointmentExternalId: string) => void;
  onOpenNote?: () => void;
}

const STATUS_PILL: Record<
  AppointmentStatus,
  { label: string; color: TagColor }
> = {
  [APPOINTMENT_STATUS.UPCOMING]: { label: 'Upcoming', color: TagColor.teal },
  [APPOINTMENT_STATUS.FULFILLED]: {
    label: 'Fulfilled',
    color: TagColor.green,
  },
  [APPOINTMENT_STATUS.CANCELLED_BY_PATIENT]: {
    label: 'Cancelled',
    color: TagColor.marigold,
  },
  [APPOINTMENT_STATUS.NO_SHOW]: { label: 'No show', color: TagColor.pink },
};

const userTypeFor = (
  type: AppointmentParticipant['type'],
): AvatarProps['userType'] => {
  switch (type) {
    case PARTICIPANT_TYPE.PROVIDER:
      return 'provider';
    case PARTICIPANT_TYPE.SUPPORT:
      return 'support';
    default:
      return 'patient';
  }
};

const InfoItem = ({
  icon,
  children,
}: {
  icon: FAIconName;
  children: React.ReactNode;
}) => (
  <ChakraFlex alignItems="center" gap="6px">
    <Icon name={icon} color="neutral.secondary" size="sm" />
    <ChakraText fontSize="13px" color="neutral.secondary">
      {children}
    </ChakraText>
  </ChakraFlex>
);

const AppointmentCard = ({
  patient,
  appointment,
  isPast = false,
  showJoin = false,
  onJoin,
  onOpenNote,
}: AppointmentCardProps) => {
  const history = useHistory();
  const { trackEvent } = useAnalytics() ?? {};

  const {
    appointmentExternalId,
    appointmentTime,
    appointmentType,
    durationMinutes,
    appointmentTypeLabel,
    consentToRecord,
    status,
    participants,
  } = appointment;

  const isUpcoming = status === APPOINTMENT_STATUS.UPCOMING;
  const pill = STATUS_PILL[status];
  const timeRange = formatAppointmentTimeRange(
    appointmentTime,
    durationMinutes,
  );

  const assignees: AvatarProps[] = (participants ?? []).map((p) => ({
    id: p.externalId,
    name: p.name,
    size: 'xs',
    src: p.avatarUrl ?? undefined,
    userType: userTypeFor(p.type),
  }));

  const tooltipWrapper = ({
    children,
    providerExternalId,
  }: {
    children: React.ReactNode;
    providerExternalId: string;
  }) => {
    const participant = (participants ?? []).find(
      (p) => p.externalId === providerExternalId,
    );
    return (
      <TooltipAvatar
        id={`participant-tooltip-${providerExternalId}`}
        userName={participant?.name ?? ''}
        userType={userTypeFor(participant?.type ?? PARTICIPANT_TYPE.PROVIDER)}
        placement="top"
      >
        {children}
      </TooltipAvatar>
    );
  };

  return (
    <ChakraBox
      data-testid={`appointment-card-${appointmentExternalId}`}
      padding="14px 0"
      borderTop="1px solid"
      borderColor="neutral.line"
    >
      <PatientIdentity
        patient={patient}
        onClick={() =>
          history.push(`${Path.MAUD_PATIENTS}/${patient.patientExternalId}`)
        }
      />

      <ChakraFlex
        marginTop="10px"
        marginLeft="42px"
        padding="12px 16px"
        borderRadius="8px"
        border="1px solid"
        borderColor="neutral.line"
        bg={
          isPast ? 'neutral.background.secondary' : 'neutral.background.primary'
        }
        alignItems="center"
        gap="12px"
        cursor={onOpenNote ? 'pointer' : 'default'}
        onClick={() => {
          if (onOpenNote) {
            trackEvent?.(ProviderDashboardEvents.myPatientsAppointmentClicked, {
              appointmentType,
              Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
            });
            onOpenNote();
          }
        }}
        role={onOpenNote ? 'button' : undefined}
        tabIndex={onOpenNote ? 0 : undefined}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (onOpenNote && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            trackEvent?.(ProviderDashboardEvents.myPatientsAppointmentClicked, {
              appointmentType,
              Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
            });
            onOpenNote();
          }
        }}
        _hover={onOpenNote ? { borderColor: 'neutral.tertiary' } : undefined}
      >
        <ChakraBox flex="1" minWidth="0">
          <ChakraText
            fontSize="15px"
            fontWeight="500"
            color="neutral.primary"
            marginBottom="6px"
          >
            {appointmentTypeLabel}
          </ChakraText>
          <ChakraFlex alignItems="center" gap="16px" flexWrap="wrap">
            {timeRange && (
              <InfoItem icon={FAIconName.clockRegular}>{timeRange}</InfoItem>
            )}
            {consentToRecord && isUpcoming && (
              <InfoItem icon={FAIconName.checkRegular}>
                Consent to record
              </InfoItem>
            )}
          </ChakraFlex>
        </ChakraBox>

        {pill && (
          <ChakraBox
            flexShrink={0}
            data-testid={`appointment-status-${appointmentExternalId}`}
          >
            <Tag
              color={pill.color}
              id={`appointment-status-tag-${appointmentExternalId}`}
              value={pill.label}
            />
          </ChakraBox>
        )}

        {assignees.length > 0 && (
          <ChakraBox flexShrink={0}>
            <AvatarGroup
              assignees={assignees}
              id={`appointment-participants-${appointmentExternalId}`}
              tooltipWrapper={tooltipWrapper}
            />
          </ChakraBox>
        )}

        {showJoin && (
          <ChakraBox flexShrink={0}>
            <FireflyButton
              id={`appointment-join-${appointmentExternalId}`}
              size="small"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                trackEvent?.(
                  ProviderDashboardEvents.myPatientsJoinSessionClicked,
                  {
                    appointmentType,
                    Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
                  },
                );
                onJoin?.(appointmentExternalId);
              }}
            >
              Join session
            </FireflyButton>
          </ChakraBox>
        )}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default AppointmentCard;
